import {
    Button, Grid, makeStyles, Paper, Typography, Divider, Snackbar
} from "@material-ui/core";
import React, { useEffect, useState } from "react";
import _ from 'lodash';

import {
    DndContext, 
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
    DragOverlay,
    useDroppable
} from '@dnd-kit/core';
import {
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
    verticalListSortingStrategy,
} from '@dnd-kit/sortable';

import { SortableItem } from './SortableItem'; // Import the separate SortableItem component

// Droppable area for content section
function ContentDroppable({ children }) {
    const { setNodeRef } = useDroppable({
        id: 'content-area'
    });
    
    const style = {
        padding: '16px',
        backgroundColor: '#f5f5f5',
        minHeight: '300px',
        border: '2px dashed #ccc',
        borderRadius: '4px'
    };
    
    return (
        <div ref={setNodeRef} style={style}>
            {children}
        </div>
    );
}

// Droppable area for available docs section
function AvailableDroppable({ children }) {
    const { setNodeRef } = useDroppable({
        id: 'available-area'
    });
    
    const style = {
        minHeight: '100px',
    };
    
    return (
        <div ref={setNodeRef} style={style}>
            {children}
        </div>
    );
}

const useStyles = makeStyles((theme) => ({
    root: {
        display: 'flex',
        flexDirection: 'row',
        margin: 'auto',
    },
    paper: {
        width: '100%',
        height: '100%',
        maxWidth:'1200px',
        padding: theme.spacing(2),
    },
    row: {
        display: 'flex',
        alignItems: 'center',
    },
    modal: {
        width: '250px',
        padding: '16px',
        margin: '0 auto',
        marginTop: '50vh',
        backgroundColor: 'rgb(49, 49, 49)',
        border: '1px solid black'
    },
    docItem: {
        marginBottom: theme.spacing(1),
        width: '100%'
    },
    contentArea: {
        minHeight: '300px',
        padding: theme.spacing(2),
        backgroundColor: '#f9f9f9',
        border: '1px dashed #ccc',
        borderRadius: '4px'
    },
    saveButton: {
        marginTop: theme.spacing(2),
    }
}));

const AccessibilitySettingsCard = (props) => {
    const styles = useStyles();

    // Track documents in the content area and left panel
    const [docsInContent, setDocsInContent] = useState([]);
    const [availableDocs, setAvailableDocs] = useState([]);
    
    // Track the actively dragging item
    const [activeId, setActiveId] = useState(null);
    
    // Track if changes have been made since last save
    const [hasChanges, setHasChanges] = useState(false);

    // Set up sensors for drag and drop
    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                distance: 5, // Start dragging after moving 5px
            },
        }),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );

    // Initialize documents and load saved configuration
    useEffect(() => {
        if (props.accessDocs && props.accessDocs.length > 0) {
            // Create a map of all documents by their ID for easy reference
            const docsMap = props.accessDocs.reduce((map, doc) => {
                map[doc.docId] = {
                    id: doc.docId,
                    name: doc.name,
                    ...doc
                };
                return map;
            }, {});
            
            // Load saved configuration if it exists
            if (props.savedConfig && props.savedConfig.docsInContent) {
                const contentDocs = props.savedConfig.docsInContent
                    .map(id => docsMap[id])
                    .filter(doc => doc); // Filter out any null values
                
                // Remaining documents go to available list
                const remainingDocs = props.accessDocs
                    .filter(doc => !props.savedConfig.docsInContent.includes(doc.docId))
                    .map(doc => ({
                        id: doc.docId,
                        name: doc.name,
                        ...doc
                    }));
                
                setDocsInContent(contentDocs);
                setAvailableDocs(remainingDocs);
            } else {
                // Initial state - all docs in available list
                setAvailableDocs(props.accessDocs.map(doc => ({
                    id: doc.docId,
                    name: doc.name,
                    ...doc
                })));
                setDocsInContent([]);
            }
        }
    }, [props.accessDocs, props.savedConfig]);

    // Update hasChanges when document arrangements change
    useEffect(() => {
        // Check if current arrangement differs from saved config
        if (props.savedConfig) {
            const currentContentIds = docsInContent.map(doc => doc.id);
            const savedContentIds = props.savedConfig.docsInContent || [];
            
            // Compare current and saved content area docs
            const orderChanged = !_.isEqual(currentContentIds, savedContentIds);
            
            setHasChanges(orderChanged);
        } else if (docsInContent.length > 0) {
            // If there's no saved config but we have docs in content, mark as changed
            setHasChanges(true);
        }
    }, [docsInContent, props.savedConfig]);

    // Handle drag start
    const handleDragStart = (event) => {
        setActiveId(event.active.id);
        console.log('Drag start:', event.active.id);
    };

    // Handle drag end
    const handleDragEnd = (event) => {
        const { active, over } = event;
        
        if (!over) {
            setActiveId(null);
            return;
        }

        console.log('Drag end:', { 
            active: active.id, 
            over: over.id,
            activeInContent: docsInContent.some(doc => doc.id === active.id),
            overInContent: docsInContent.some(doc => doc.id === over.id),
            activeInAvailable: availableDocs.some(doc => doc.id === active.id),
            overIsContentArea: over.id === 'content-area',
            overIsAvailableArea: over.id === 'available-area',
        });

        // Handle moving between containers
        if (over.id === 'content-area' && availableDocs.some(doc => doc.id === active.id)) {
            // Moving from available to content
            console.log('Moving from available to content');
            const docToMove = availableDocs.find(doc => doc.id === active.id);
            setDocsInContent([...docsInContent, docToMove]);
            setAvailableDocs(availableDocs.filter(doc => doc.id !== active.id));
        } else if (over.id === 'available-area' && docsInContent.some(doc => doc.id === active.id)) {
            // Moving from content to available
            console.log('Moving from content to available');
            const docToMove = docsInContent.find(doc => doc.id === active.id);
            setAvailableDocs([...availableDocs, docToMove]);
            setDocsInContent(docsInContent.filter(doc => doc.id !== active.id));
        } else if (docsInContent.some(doc => doc.id === active.id) && docsInContent.some(doc => doc.id === over.id)) {
            // Reordering within content container
            console.log('Reordering within content');
            const oldIndex = docsInContent.findIndex(doc => doc.id === active.id);
            const newIndex = docsInContent.findIndex(doc => doc.id === over.id);
            
            if (oldIndex !== newIndex) {
                const newItems = arrayMove(docsInContent, oldIndex, newIndex);
                console.log('New order:', newItems.map(i => i.id));
                setDocsInContent(newItems);
            }
        }
        
        setActiveId(null);
    };

    // Save the current configuration
    const handleSave = () => {
        // Extract IDs of docs in content area in their current order
        const contentDocIds = docsInContent.map(doc => doc.id);
        
        // Prepare data for saving
        const saveData = {
            docsInContent: contentDocIds,
            lastUpdated: new Date()
        };
        
        console.log('Saving configuration:', saveData);
        
        // Call the save handler from props
        if (props.handleSaveConfig) {
            props.handleSaveConfig(saveData);
        }
        
        setHasChanges(false);
    };

    // Find the active document for overlay
    const activeDoc = [...availableDocs, ...docsInContent].find(doc => doc.id === activeId);

    return (
        <DndContext 
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
        >
            <Grid container spacing={2} className={styles.root}>
                <Grid item xs={3}>
                    <Paper className={styles.paper}>
                        <Grid container item spacing={2}>
                            <Grid item xs={12}>
                                <Typography variant={'h6'}>Accessibility Documents</Typography>
                                <Divider />
                            </Grid>
                            <Typography color="textSecondary" align="center" style={{ padding: '1px' }}>
                                   <small>Drag documents back here to remove</small>
                                </Typography>
                            <Grid item xs={12}>                             

                                <AvailableDroppable>
                                    <SortableContext 
                                        items={availableDocs.map(doc => doc.id)}
                                        strategy={verticalListSortingStrategy}
                                    >
                                        {availableDocs.map((doc) => (
                                            <SortableItem 
                                                key={doc.id}
                                                id={doc.id}
                                                name={doc.name}
                                                isInContentArea={false}
                                            />
                                        ))}
                                    </SortableContext>

                                   
                                </AvailableDroppable>
                            </Grid>
                        </Grid>
                    </Paper>
                </Grid>
                <Grid item xs={9}>
                    <Paper className={styles.paper}>
                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                <Grid container display='flex'>
                                    <Grid item xs={12}>
                                        <Typography variant={'h6'}>Accessibility Page (Any documents here will display on the main store page under Accessibility)</Typography>
                                    </Grid>
                                </Grid>
                                <Divider />
                            </Grid>
                            <Grid item xs={12}>
                                <ContentDroppable>
                                    <SortableContext 
                                        items={docsInContent.map(doc => doc.id)}
                                        strategy={verticalListSortingStrategy}
                                    >
                                        {docsInContent.map((doc) => (
                                            <SortableItem 
                                                key={doc.id}
                                                id={doc.id}
                                                name={doc.name}
                                                isInContentArea={true}
                                            />
                                        ))}
                                    </SortableContext>
                                    {docsInContent.length === 0 && (
                                        <Typography color="textSecondary" align="center" style={{ padding: '1rem' }}>
                                            Drag documents here to display them
                                        </Typography>
                                    )}
                                </ContentDroppable>
                            </Grid>
                            <Grid item xs={12}>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    className={styles.saveButton}
                                    onClick={handleSave}
                                    disabled={!hasChanges || props.saving}
                                >
                                    {props.saving ? 'Saving...' : 'Save Configuration'}
                                </Button>
                            </Grid>
                        </Grid>
                    </Paper>
                </Grid>
                {/* Save notification snackbar - using props for consistency */}
                <Snackbar
                    open={hasChanges && !props.saving}
                    message={'Save configuration?'}
                    anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'center'
                    }}
                    action={
                        <React.Fragment>
                            <Button
                                color='primary'
                                size='small'
                                variant="contained"
                                onClick={handleSave}
                                disabled={props.saving}
                            >
                                Save
                            </Button>
                            <Button
                                color='secondary'
                                variant="contained"
                                size='small'
                                onClick={() => props.cancelSave && props.cancelSave()}
                                disabled={props.saving}
                            >
                                Cancel
                            </Button>
                        </React.Fragment>
                    }
                />
            </Grid>
            
            {/* Drag Overlay */}
            <DragOverlay>
                {activeId ? (
                    <div style={{
                        padding: '8px',
                        border: '1px solid #ccc',
                        backgroundColor: '#fff',
                        borderRadius: '4px',
                        width: '200px',
                        boxShadow: '0 5px 10px rgba(0,0,0,0.2)'
                    }}>
                        {activeDoc?.name}
                    </div>
                ) : null}
            </DragOverlay>
        </DndContext>
    );
};

export default AccessibilitySettingsCard;